/**
 * Match In-Progress View
 *
 * Thin orchestrator that composes sub-components for the active match screen:
 * MapHero, TeamRosters, ReportResultPanel, MatchActionBar.
 */

import type { QueueRankConfig } from "@rivaled/shared";
import { getTeamNames } from "@rivaled/shared";
import { Gamepad2, Layers, Swords, Users } from "lucide-react";
import { useEffect, useState } from "react";

import { useSession } from "../../lib/auth-client";
import { getPartyColor } from "../../match-flow/lib/party-colors";
import { MapHero } from "./MapHero";
import { MatchActionBar } from "./MatchActionBar";
import { formatModePill } from "./matchHelpers";
import { ReportResultPanel } from "./ReportResultPanel";
import { TeamRosters } from "./TeamRosters";
import type { MatchData } from "./matchHelpers";

// ---------------------------------------------------------------------------
// Live duration timer
// ---------------------------------------------------------------------------

function useLiveDuration(startedAt: string | null) {
  const [duration, setDuration] = useState("0:00");

  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();
    const tick = () => {
      const secs = Math.max(0, Math.floor((Date.now() - start) / 1000));
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const s = secs % 60;
      setDuration(
        h > 0
          ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
          : `${m}:${String(s).padStart(2, "0")}`,
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  return duration;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MatchInProgressView({ match }: { match: MatchData }) {
  const { data: session } = useSession();

  const currentUserId = session?.user?.id ?? "";
  const teamCount = match.teamCount ?? 2;
  const teamSize = match.teamSize ?? 5;
  const teamNames = getTeamNames(match.id, teamCount);
  const duration = useLiveDuration(match.startedAt);
  const isParticipant = match.players.some((p) => p.userId === currentUserId);
  const totalPlayers = match.players.length;
  const isFFA = teamSize === 1;
  const rankConfig = (match.rankConfig ?? null) as QueueRankConfig | null;

  const myPartyId = match.players.find((p) => p.userId === currentUserId)?.partyId ?? null;

  // Group players by team (0-indexed)
  const teamGroups = new Map<number, MatchData["players"]>();
  for (const player of match.players) {
    if (!teamGroups.has(player.team)) teamGroups.set(player.team, []);
    teamGroups.get(player.team)!.push(player);
  }

  const myTeam = match.players.find((p) => p.userId === currentUserId)?.team;
  const sortedTeams = [...teamGroups.entries()].sort(([a], [b]) => a - b);
  const isVsLayout = teamCount === 2 && sortedTeams.length === 2 && !isFFA;
  const queueNameIncludesSize =
    match.queueName?.includes(formatModePill(teamCount, teamSize)) ?? false;

  // For 2-team VS: always put my team on the left
  const vsTeams = isVsLayout
    ? myTeam === sortedTeams[1]?.[0]
      ? [sortedTeams[1], sortedTeams[0]]
      : sortedTeams
    : sortedTeams;

  const teamAvgElo = (teamNum: number) => {
    const players = teamGroups.get(teamNum) ?? [];
    if (players.length === 0) return 0;
    return Math.round(players.reduce((sum, p) => sum + (p.eloAtMatch ?? 0), 0) / players.length);
  };

  const getPlayerPartyColor = (partyId: string | null) => getPartyColor(partyId, myPartyId);

  return (
    <div className="relative space-y-4">
      {/* Ambient background glow */}
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-full max-w-3xl h-72 bg-brand-500/5 rounded-full blur-3xl pointer-events-none" />

      <MapHero
        mapImageUrl={match.mapImageUrl}
        mapName={match.mapName}
        gameName={match.gameName}
        gameIconUrl={match.gameIconUrl}
        duration={duration}
      />

      {/* Info Pills */}
      <div className="flex flex-wrap items-center gap-2">
        {match.gameName && (
          <div className="flex items-center gap-1.5 bg-surface-800/60 border border-surface-700/30 rounded-lg px-2.5 py-1.5">
            {match.gameIconUrl ? (
              <img src={match.gameIconUrl} alt="" className="h-4 w-4 rounded-sm object-cover" />
            ) : (
              <Gamepad2 className="h-3.5 w-3.5 text-surface-500" />
            )}
            <span className="text-xs font-display font-semibold text-text-primary">
              {match.gameName}
            </span>
          </div>
        )}
        {match.queueName && (
          <div className="flex items-center gap-1.5 bg-surface-800/60 border border-surface-700/30 rounded-lg px-2.5 py-1.5">
            {match.queueIconUrl ? (
              <img src={match.queueIconUrl} alt="" className="h-4 w-4 rounded-sm object-cover" />
            ) : (
              <Layers className="h-3.5 w-3.5 text-surface-500" />
            )}
            <span className="text-xs font-display font-semibold text-text-primary">
              {match.queueName}
            </span>
          </div>
        )}
        {!queueNameIncludesSize && (
          <div className="flex items-center gap-1.5 bg-surface-800/60 border border-surface-700/30 rounded-lg px-2.5 py-1.5">
            {isFFA ? (
              <Users className="h-3.5 w-3.5 text-surface-500" />
            ) : (
              <Swords className="h-3.5 w-3.5 text-surface-500" />
            )}
            <span className="text-xs font-display font-semibold text-text-primary">
              {formatModePill(teamCount, teamSize)}
            </span>
          </div>
        )}
      </div>

      <TeamRosters
        players={match.players}
        currentUserId={currentUserId}
        teamNames={teamNames}
        myTeam={myTeam}
        teamCount={teamCount}
        isFFA={isFFA}
        isVsLayout={isVsLayout}
        sortedTeams={sortedTeams}
        vsTeams={vsTeams}
        teamAvgElo={teamAvgElo}
        rankConfig={rankConfig}
        totalPlayers={totalPlayers}
        getPartyColor={getPlayerPartyColor}
      />

      {/* Match Actions (participants only) */}
      {isParticipant && (
        <div className="space-y-3 pt-2">
          <ReportResultPanel
            matchId={match.id}
            players={match.players}
            currentUserId={currentUserId}
            teamNames={teamNames}
            myTeam={myTeam}
            teamCount={teamCount}
            isFFA={isFFA}
            sortedTeams={sortedTeams}
            totalPlayers={totalPlayers}
          />
          <MatchActionBar
            matchId={match.id}
            currentUserId={currentUserId}
            players={match.players}
            totalPlayers={totalPlayers}
          />
        </div>
      )}

      {/* Spectator Badge */}
      {!isParticipant && (
        <div className="flex items-center gap-2.5 rounded-xl border border-surface-700/40 bg-surface-800/40 px-4 py-3">
          <div className="h-2 w-2 rounded-full bg-brand-500 animate-pulse" />
          <span className="text-xs font-display font-semibold text-surface-400 uppercase tracking-wider">
            Spectating
          </span>
        </div>
      )}

      {/* Match Footer */}
      <div className="flex items-center justify-center pt-1 pb-2">
        <span className="text-xs text-surface-600 font-mono">{match.id.slice(0, 8)}</span>
      </div>
    </div>
  );
}
